import { useState, useEffect, useRef } from "react";
import { withTranslation } from "react-i18next";
import {
  Alert,
  Box,
  CircularProgress,
  Fade,
  Typography,
} from "@mui/material";
import Snackbar from "@plannel/components/Snackbar";
import BarChartIcon from "@mui/icons-material/BarChart";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import i18n from "i18next";
import { createEmbeddingContext } from "amazon-quicksight-embedding-sdk";
import quickSightService from "@plannel/services/system/quicksight-service";

const CustomReport = ({ t, dashboardId }) => {
  const [embedLoading, setEmbedLoading] = useState(false);
  const [embedError, setEmbedError] = useState("");
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const embeddingContextRef = useRef(null);
  const dashboardRef = useRef(null);
  const containerRef = useRef(null); // 실제 iframe이 렌더링되는 DOM 요소 참조
  const currentTokenRef = useRef(0); // 순서 보장용 버전 토큰

  useEffect(() => {
    if (!dashboardId) return;

    const token = ++currentTokenRef.current; // 호출마다 고유 번호 부여

    const run = async () => {
      setEmbedLoading(true);
      setEmbedError("");

      // 이전 대시보드 SDK 정리 (이벤트 리스너 · iframe 누수 방지)
      if (dashboardRef.current) {
        try { dashboardRef.current.remove(); } catch (_) { /* 무시 */ }
      }
      dashboardRef.current = null;

      // DOM 직접 초기화
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }

      // dashboardId가 바뀔 때마다 새 EmbeddingContext 생성 (SDK 내부 상태 초기화)
      embeddingContextRef.current = await createEmbeddingContext();
      if (token !== currentTokenRef.current) return; // 오래된 호출이면 중단

      try {
        const res = await quickSightService.getEmbedUrl(dashboardId);
        if (token !== currentTokenRef.current) return; // 오래된 호출이면 중단

        const frameOptions = {
          url: res.data.embedUrl,
          container: containerRef.current,
          width: "100%",
          height: "100%",
          resizeHeightOnSizeChangedEvent: false,
        };

        const contentOptions = {
          locale: i18n.language, // 콘솔/PDF와 다른 로케일(예: "All")로 나오는 것 방지 — 앱 언어로 고정
          toolbarOptions: { export: true, undoRedo: true, reset: true },
          attributionOptions: { overlayContent: false },
        };

        // embedDashboard 내부에서 appendChild를 통한 강제 DOM 변경이 일어남.
        dashboardRef.current = await embeddingContextRef.current.embedDashboard(
          frameOptions,
          contentOptions
        );

      } catch (e) {
        if (token !== currentTokenRef.current) return; // 오래된 호출의 에러는 무시
        const msg = e?.response?.data?.message || t("MSG_FetchEmbedUrlFailed");
        setEmbedError(String(msg));
        setSnackInfo({ open: true, severity: "error", content: String(msg) });
      } finally {
        if (token === currentTokenRef.current) setEmbedLoading(false); // 최신 호출만 로딩 해제
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId]); // t 제외 — 언어 변경 시 대시보드 재로드 방지

  return (
    <Box sx={{ width: "100%", height: "100%", position: "relative", bgcolor: "grey.50" }}>

      {/* 로딩 오버레이 */}
      {embedLoading && (
        <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, bgcolor: "grey.50", zIndex: 1 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            {t("MSG_Loading")}
          </Typography>
        </Box>
      )}

      {/* dashboardId 없음 */}
      {!dashboardId && !embedLoading && (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
          <BarChartIcon sx={{ fontSize: 64, color: "grey.300" }} />
          <Typography variant="body2" color="text.disabled">
            {t("MSG_SelectDashboardHint")}
          </Typography>
        </Box>
      )}

      {/* embed 실패 */}
      {!embedLoading && embedError && (
        <Fade in>
          <Box sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, px: 4 }}>
            <LockOutlinedIcon sx={{ fontSize: 64, color: "error.light" }} />
            <Typography variant="subtitle1" fontWeight={600} color="error.main">
              {t("MSG_EmbedAccessDeniedTitle")}
            </Typography>
            <Alert severity="error" sx={{ mt: 1 }}>{embedError}</Alert>
          </Box>
        </Fade>
      )}

      {/* SDK iframe 컨테이너 */}
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          height: "100%",
          display: !embedLoading && !embedError && dashboardId ? "block" : "none",
          "& iframe": { width: "100% !important", height: "100% !important", border: "none" },
        }}
      />

      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo((prev) => ({ ...prev, open: false }))}
        content={snackInfo.content}
        severity={snackInfo.severity}
      />
    </Box>
  );
};

export default withTranslation()(CustomReport);
